<template>
  <div class="analysis-pack-page">
    <div class="analysis-pack-content">
      <div v-if="loading" class="loading-state">
        <BaseSpinner />
        <p>Loading files...</p>
      </div>

      <div v-else class="analysis-pack-sections">
        <div class="analysis-pack-section-header">
          <h2 class="analysis-pack-section-title">Company Analysis Pack</h2>
          <div class="status-legend">
            <span class="status-legend-item">
              <span class="status-badge status-completed"><i class="las la-check"></i></span>
              <span>Completed</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-pending"><i class="las la-clock"></i></span>
              <span>Pending</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-processing"><i class="las la-sync"></i></span>
              <span>Processing</span>
            </span>
            <span class="status-legend-item">
              <span class="status-badge status-error"><i class="las la-times"></i></span>
              <span>Error</span>
            </span>
          </div>
        </div>

        <div class="pack-dashboard-header">
          <i :class="allRequirementsMet ? 'las la-check-circle pack-icon-ok' : 'las la-clipboard-list'"></i>
          <div>
            <h3>Required Documents</h3>
            <p>
              {{ requirementsMetCount }} of {{ requirementsTotal }} required documents uploaded.
              Select a document type to review uploaded files.
            </p>
          </div>
        </div>

        <div class="req-doc-grid">
          <button
            v-for="fileType in requiredFileTypes"
            :key="fileType"
            type="button"
            class="req-doc-card"
            :class="{
              met: isRequirementMet(fileType),
              selected: selectedFileType === fileType
            }"
            @click="selectFileType(fileType)"
          >
            <i
              :class="isRequirementMet(fileType) ? 'las la-check' : 'las la-times'"
              class="req-doc-status"
            ></i>
            <span class="req-doc-label">{{ formatFileType(fileType) }}</span>
            <span v-if="isRequirementMet(fileType)" class="req-doc-value">
              {{ getFilesForType(fileType).length }}
            </span>
            <span v-else class="req-doc-missing">Missing</span>
          </button>
        </div>

        <transition name="pack-files">
          <div v-if="selectedFileType" class="pack-block pack-files-panel">
            <h3 class="subsection-title">{{ formatFileType(selectedFileType) }} Files</h3>
            <transition name="pack-files-inner" mode="out-in">
              <RequirementFilesList
                :key="selectedFileType"
                :files="getFilesForType(selectedFileType)"
                :file-type="selectedFileType"
              />
            </transition>
          </div>
        </transition>

        <div class="section-header">
          <div class="section-header-actions">
            <p v-if="!allRequirementsMet" class="requirements-hint">
              Upload bank statements, Emirates ID, and trade license before generating.
            </p>
            <span v-else class="requirements-hint requirements-hint-ready">
              All required documents are ready.
            </span>
            <BaseButton
              stylevariant="primary"
              :cus_style="{ width: 'auto' }"
              class="upload-section-btn"
              :disabled="!allRequirementsMet || generating"
              :loading="generating"
              @click="generateAnalysisPack"
            >
              <i v-if="!generating" class="las la-play"></i>
              {{ generating ? 'Generating...' : 'Generate Analysis Pack' }}
            </BaseButton>
          </div>
        </div>

        <div v-if="showGenerationProgress" class="pack-block">
          <h3 class="subsection-title">Generation Progress</h3>
          <div class="progress-container">
            <div v-if="progressData" class="progress-info">
              <div class="progress-status" :class="progressData.status">
                <i :class="getStatusIcon(progressData.status)"></i>
                <span>{{ progressData.status || 'Processing' }}</span>
              </div>
              <div v-if="progressData.message" class="progress-message">
                {{ progressData.message }}
              </div>
              <div
                v-if="progressData.progress !== undefined && progressData.progress !== null"
                class="progress-bar-container"
              >
                <div class="progress-bar">
                  <div
                    class="progress-bar-fill"
                    :style="{ width: `${progressData.progress}%` }"
                  ></div>
                </div>
                <div class="progress-percentage">{{ progressData.progress }}%</div>
              </div>
              <div v-if="progressData.error" class="progress-error">
                <i class="las la-exclamation-triangle"></i>
                <span>{{ progressData.error }}</span>
              </div>
            </div>
            <div v-else class="progress-info">
              <div class="progress-status processing">
                <i class="las la-spinner la-spin"></i>
                <span>Initializing...</span>
              </div>
            </div>
          </div>
        </div>

        <div v-if="analysisPackFile" class="pack-block">
          <h3 class="subsection-title">Generated Analysis Pack</h3>
          <div class="document-card">
            <div class="document-card-content">
              <div class="document-icon-large">
                <i class="las la-file-excel"></i>
              </div>
              <span class="document-name">{{ analysisPackFile.name }}</span>
              <span class="document-date">
                {{ formatUploadDate(analysisPackFile.uploaded_at) }}
              </span>
              <span class="status-badge" :class="getDocumentStatusClass(analysisPackFile.scan_status)">
                <i :class="getDocumentStatusIcon(analysisPackFile.scan_status)"></i>
              </span>
              <button
                v-if="analysisPackFile.status === 'completed' || analysisPackFile.status === 'success'"
                type="button"
                class="view-btn"
                @click.stop="viewAnalysisPackFile"
                title="View Document"
              >
                <i class="las la-external-link-alt"></i>
              </button>
              <BaseSpinner
                v-if="generatingReport === getAnalysisPackFileId()"
                size="small"
                class="report-btn-spinner"
              />
              <button
                v-else
                type="button"
                class="report-btn"
                :disabled="isReportDisabled(analysisPackFile)"
                @click.stop="generateReport(analysisPackFile)"
                title="Generate Report"
              >
                <i class="las la-chart-bar"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from "@/services/chatbot.service";
import RequirementFilesList from "./components/RequirementFilesList.vue";
import BaseSpinner from "@/components/UI/BaseSpinner.vue";
import BaseButton from '@/components/UI/BaseButton.vue';

export default {
  name: 'AnalysisPackPage',
  components: {
    RequirementFilesList,
    BaseSpinner,
    BaseButton
  },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: false,
      currentFiles: [],
      requiredFileTypes: [
        'bank_statement',
        'emirates_id',
        'trade_license',
      ],
      generating: false,
      selectedFileType: null,
      websocket: null,
      progressData: null,
      analysisPackFile: null,
      generatingReport: null
    }
  },
  computed: {
    allRequirementsMet() {
      return this.requiredFileTypes.every(type => this.isRequirementMet(type));
    },
    requirementsMetCount() {
      return this.requiredFileTypes.filter(type => this.isRequirementMet(type)).length;
    },
    requirementsTotal() {
      return this.requiredFileTypes.length;
    },
    isAnalysisPackComplete() {
      return !!this.analysisPackFile;
    },
    showGenerationProgress() {
      return this.generating || (!!this.progressData && !this.isAnalysisPackComplete);
    }
  },
  async mounted() {
    await this.loadFiles();
    this.connectWebSocket();
  },
  beforeDestroy() {
    this.disconnectWebSocket();
  },
  methods: {
    async loadFiles() {
      this.loading = true;
      this.currentFiles = [];
      let page = 1;
      try {
        let filesData = await ChatbotService.getUploads(this.candidateId, { page: page});
        this.currentFiles = [...this.currentFiles, ...filesData.data];
        while (filesData.next !== null) {
          filesData = await ChatbotService.getUploads(this.candidateId, { page: page});
          this.currentFiles = [...this.currentFiles, ...filesData.data];
          page++;
        }
      } catch (err) {
        console.error('Error loading files:', err);
        this.$bvToast.toast('Failed to load files.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        this.currentFiles = [];
      } finally {
        this.setAnalysisPackFromFiles();
        this.loading = false;
      }
    },

    setAnalysisPackFromFiles() {
      const packs = this.currentFiles.filter((file) => {
        const fileType = file.type || file.document_type;
        return fileType === 'analysis_pack';
      });
      this.analysisPackFile = this.getLatestUpload(packs);
    },

    getLatestUpload(documents) {
      if (!Array.isArray(documents) || documents.length === 0) return null;

      return documents.reduce((latest, doc) => {
        const docDate = new Date(doc.uploaded_at || doc.scan_completed_at || 0);
        const latestDate = new Date(latest.uploaded_at || latest.scan_completed_at || 0);
        return docDate > latestDate ? doc : latest;
      });
    },
    
    isRequirementMet(fileType) {
      return this.currentFiles.some(file => {
        const fileTypeValue = file.type || file.document_type;
        if (fileType === 'trade_license') {
          return fileTypeValue === 'trade_license' || fileTypeValue === 'trade_licence';
        }
        return fileTypeValue === fileType;
      });
    },
    
    getFilesForType(fileType) {
      return this.currentFiles.filter(file => {
        const fileTypeValue = file.type || file.document_type;
        if (fileType === 'trade_license') {
          return fileTypeValue === 'trade_license' || fileTypeValue === 'trade_licence';
        }
        return fileTypeValue === fileType;
      });
    },
    
    selectFileType(fileType) {
      this.selectedFileType = this.selectedFileType === fileType ? null : fileType;
    },
    
    async generateAnalysisPack() {
      if (!this.allRequirementsMet) {
        this.$bvToast.toast('Please ensure all required file types are uploaded.', {
          title: 'Missing Requirements',
          variant: 'warning',
          solid: true,
          autoHideDelay: 5000
        });
        return;
      }
      
      this.generating = true;
      this.progressData = null;
      this.analysisPackFile = null;
      
      try {
        await ChatbotService.generateCompanyAnalysisPack(this.candidateId);
      } catch (err) {
        console.error('Error initiating company analysis pack generation:', err);
        const errorMessage = err.response?.data?.message || err.message || 'Failed to initiate analysis pack generation. Please try again.';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
        this.generating = false;
        this.progressData = null;
      }
    },
    
    connectWebSocket() {
      try {
        const wsUrl = `${process.env.VUE_APP_SOCKET_URL}/analysis-pack/${this.candidateId}/`;
        this.websocket = new WebSocket(wsUrl);
        
        this.websocket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleWebSocketMessage(data);
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
      } catch (error) {
        console.error('Error connecting to Analysis Pack WebSocket:', error);
      }
    },
    
    disconnectWebSocket() {
      if (this.websocket) {
        this.websocket.close();
        this.websocket = null;
      }
    },
    
    handleWebSocketMessage(data) {
      if (data.type === 'progress') {
        this.progressData = {
          status: data.status,
          message: data.message || '',
          progress: data.progress || 0,
          upload_id: data.upload_id,
          error: data.error
        };
        
        if (data.status === 'completed' || data.status === 'success') {
          this.generating = false;
          this.progressData = null;
          this.$bvToast.toast('Company Analysis Pack generated successfully.', {
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
          this.fetchAnalysisPackFile();
        }
        
        if (data.status === 'failed' || data.status === 'error' || data.error) {
          this.generating = false;
          const errorMessage = data.error || data.message || 'Failed to generate analysis pack.';
          this.$bvToast.toast(errorMessage, {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          });
        }
      }
    },
    
    async fetchAnalysisPackFile() {
      try {
        const filesData = await ChatbotService.getUploads(this.candidateId, {
          type: 'analysis_pack',
          page: 1,
          page_size: 10
        });
        const files = Array.isArray(filesData)
          ? filesData
          : (filesData.files || filesData.uploads || filesData.data || []);

        this.analysisPackFile = this.getLatestUpload(files);
      } catch (error) {
        console.error('Error fetching analysis pack file:', error);
      }
    },

    formatUploadDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    },

    getDocumentStatusClass(status) {
      if (status === 'completed' || status === 'success') return 'status-completed';
      if (status === 'processing' || status === 'extracting') return 'status-processing';
      if (status === 'failed' || status === 'error') return 'status-error';
      return 'status-pending';
    },

    getDocumentStatusIcon(status) {
      if (status === 'completed' || status === 'success') return 'las la-check';
      if (status === 'processing' || status === 'extracting') return 'las la-sync';
      if (status === 'failed' || status === 'error') return 'las la-times';
      return 'las la-clock';
    },

    viewAnalysisPackFile() {
      const fileId = this.analysisPackFile?.id || this.analysisPackFile?.file_id;
      if (!fileId) return;

      this.$router.push({
        name: 'chatbot-file-details',
        params: {
          candidateId: this.candidateId,
          fileId
        }
      });
    },

    getAnalysisPackFileId() {
      return this.analysisPackFile?.id || this.analysisPackFile?.file_id || null;
    },

    isReportDisabled(file) {
      if (!file) return true;

      const fileId = file.id || file.file_id;
      return this.generatingReport === fileId
        || ['scanning', 'extracting', 'pending'].includes(file.status);
    },

    async generateReport(file) {
      const fileId = file?.id || file?.file_id;
      if (!fileId || this.isReportDisabled(file)) {
        return;
      }

      this.generatingReport = fileId;

      try {
        const blob = await ChatbotService.generateReport(fileId);

        if (!(blob instanceof Blob)) {
          throw new Error('Invalid report data received from server');
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const baseName = (file.name || 'report').replace(/\.xlsx$/i, '');
        link.download = `${baseName} ${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating report:', error);
        this.$bvToast.toast(
          `Error generating report: ${error.message || 'Unknown error'}`,
          {
            title: 'Error',
            variant: 'danger',
            solid: true,
            autoHideDelay: 5000
          }
        );
      } finally {
        this.generatingReport = null;
      }
    },
    
    getStatusIcon(status) {
      switch (status) {
        case 'completed':
        case 'success':
          return 'las la-check-circle';
        case 'failed':
        case 'error':
          return 'las la-times-circle';
        case 'processing':
        case 'generating':
          return 'las la-spinner la-spin';
        default:
          return 'las la-info-circle';
      }
    },
    
    formatFileType(type) {
      switch (type) {
        case 'bank_statement':
          return 'Bank Statement';
        case 'emirates_id':
          return 'Emirates ID';
        case 'trade_license':
          return 'Trade License';
        case 'vat_statement':
          return 'VAT Statement';
        default:
          return type || 'Unknown';
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './index.scss';

.analysis-pack-page {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
}

.analysis-pack-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem 2rem 2rem;
}

.loading-state {
  text-align: center;
  padding: 3rem 2rem;

  p {
    color: #718096;
    font-size: 1rem;
    margin: 0;
    font-family: $brand-font-regular;
  }
}

.analysis-pack-sections {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.analysis-pack-section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 0.5rem;
}

.analysis-pack-section-title {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.status-legend {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;

  .status-legend-item {
    display: inline-flex;
    align-items: center;
    font-size: 0.8125rem;
    color: #4a5568;
    font-family: $brand-font-regular;

    .status-badge {
      width: 22px;
      height: 22px;
      font-size: 0.9rem;

      i {
        font-size: 1rem;
      }
    }
  }
}

.pack-dashboard-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  > i {
    font-size: 2rem;
    color: $color-primary;
    flex-shrink: 0;
    margin-top: 2px;

    &.pack-icon-ok {
      color: #16a34a;
    }
  }

  h3 {
    font-size: 1.125rem;
    font-weight: 600;
    color: #2d3748;
    margin: 0 0 0.25rem;
    font-family: $brand-font-medium;
  }

  p {
    color: #718096;
    font-size: 0.875rem;
    margin: 0;
    font-family: $brand-font-regular;
  }
}

.req-doc-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.req-doc-card {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.7rem 1.05rem;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
  text-align: left;
  cursor: pointer;
  min-height: 0;

  &.met {
    border-color: #bbf7d0;
    background: #f0fdf4;
  }

  &:not(.met) {
    border-color: #fecaca;
    background: #fef2f2;
  }

  &.selected {
    border-color: $color-primary;
    background: rgba($color-primary, 0.08);
  }
}

.req-doc-status {
  font-size: 1rem;
  flex-shrink: 0;
  color: #dc2626;

  .req-doc-card.met & {
    color: #16a34a;
  }
}

.req-doc-label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
  white-space: nowrap;
}

.req-doc-value {
  font-size: 0.95rem;
  color: #64748b;
  font-family: $brand-font-medium;
  font-variant-numeric: tabular-nums;

  &::before {
    content: '·';
    margin-right: 0.35rem;
    color: #cbd5e1;
  }
}

.req-doc-missing {
  font-size: 0.95rem;
  color: #b91c1c;
  font-family: $brand-font-regular;

  &::before {
    content: '·';
    margin-right: 0.35rem;
    color: #fecaca;
  }
}

.pack-block {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pack-files-panel {
  overflow: hidden;
  padding: 0.875rem 1rem;
  border: 1px solid #eef2f7;
  border-radius: 12px;
  background: #fcfdfe;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.03);
}

.pack-files-enter-active,
.pack-files-leave-active {
  transition: opacity 0.32s ease, transform 0.32s ease, max-height 0.38s ease, padding 0.32s ease;
  overflow: hidden;
}

.pack-files-enter,
.pack-files-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.pack-files-enter-to,
.pack-files-leave {
  opacity: 1;
  transform: translateY(0);
  max-height: 560px;
}

.pack-files-inner-enter-active,
.pack-files-inner-leave-active {
  transition: opacity 0.2s ease;
}

.pack-files-inner-enter,
.pack-files-inner-leave-to {
  opacity: 0;
}

.subsection-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
  font-family: $brand-font-medium;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0;
  gap: 1rem;
}

.section-header-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 1;
  min-width: 0;
  gap: 1rem;
  flex-wrap: wrap;
}

.requirements-hint {
  margin: 0;
  color: #667085;
  font-size: 0.875rem;
  font-family: $brand-font-regular;
  flex: 1;
  min-width: 200px;
}

.requirements-hint-ready {
  color: #15803d;
}

.upload-section-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  width: auto;
  padding: 0.625rem 1rem;
  background: $color-primary;
  color: $color-secondary;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  font-family: $brand-font-medium;
  flex-shrink: 0;
}

.progress-container {
  padding: 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;

  .progress-info {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .progress-status {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    font-size: 0.95rem;
    font-family: $brand-font-medium;

    i {
      font-size: 1.1rem;
    }

    &.processing,
    &.generating {
      color: #3b82f6;

      i {
        color: #3b82f6;
      }
    }

    &.completed,
    &.success {
      color: #16a34a;

      i {
        color: #16a34a;
      }
    }

    &.failed,
    &.error {
      color: #dc2626;

      i {
        color: #dc2626;
      }
    }
  }

  .progress-message {
    color: #4a5568;
    font-size: 0.9rem;
    line-height: 1.5;
    font-family: $brand-font-regular;
  }

  .progress-bar-container {
    display: flex;
    align-items: center;
    gap: 1rem;

    .progress-bar {
      flex: 1;
      height: 8px;
      background: #e2e8f0;
      border-radius: 4px;
      overflow: hidden;

      .progress-bar-fill {
        height: 100%;
        background: linear-gradient(90deg, $color-primary, lighten($color-primary, 10%));
        border-radius: 4px;
        transition: width 0.3s ease;
      }
    }

    .progress-percentage {
      font-size: 0.85rem;
      font-weight: 600;
      color: #4a5568;
      min-width: 45px;
      text-align: right;
      font-family: $brand-font-medium;
      font-variant-numeric: tabular-nums;
    }
  }

  .progress-error {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-radius: 8px;
    color: #dc2626;
    font-size: 0.9rem;
    font-family: $brand-font-regular;

    i {
      font-size: 1.1rem;
    }
  }
}

.document-card {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 0.5rem;
}

.document-card-content {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.document-icon-large {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  i {
    font-size: 1.25rem;
    color: $color-primary;
  }
}

.document-name {
  flex: 1;
  font-size: 1rem;
  font-weight: 500;
  color: #2d3748;
  font-family: $brand-font-medium;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.document-date {
  font-size: 0.875rem;
  color: #6c757d;
  font-family: $brand-font-regular;
  white-space: nowrap;
  flex-shrink: 0;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  font-size: 1rem;
  background: transparent;

  &.status-completed {
    color: #16a34a;

    i {
      color: #16a34a;
    }
  }

  &.status-processing {
    color: #3b82f6;

    i {
      color: #3b82f6;
    }
  }

  &.status-error {
    color: #dc2626;

    i {
      color: #dc2626;
    }
  }

  &.status-pending {
    color: #6b7280;

    i {
      color: #6b7280;
    }
  }

  i {
    font-size: 1.3rem;
  }
}

.view-btn {
  background: transparent;
  border: none;
  color: #4a5568;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover i {
    color: darken(#4a5568, 10%);
  }

  i {
    font-size: 1.3rem;
  }
}

.report-btn {
  background: transparent;
  border: none;
  color: #3182ce;
  padding: 0.1rem;
  margin-right: 0.2rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) i {
    color: darken(#3182ce, 10%);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  i {
    font-size: 1.3rem;
  }
}

.report-btn-spinner {
  margin-right: 0.2rem;
  flex-shrink: 0;
}

::v-deep .requirement-files {
  border-top: none;
  padding: 0;
  background: transparent;
}

::v-deep .files-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

::v-deep .file-item {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .analysis-pack-content {
    padding: 1rem;
  }

  .analysis-pack-section-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .section-header-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .upload-section-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
